import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Music, ArrowLeft, Plus, Trash2, Edit, Eye, EyeOff, Save, X, Quote 
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    instrument: 'Guitar',
    duration: '',
    text: '',
    isVisible: true
  });

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (token) {
      verifyAndFetch();
    } else {
      window.location.href = '/admin';
    }
  }, []);

  const verifyAndFetch = async () => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      fetchTestimonials();
    } catch (error) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin';
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/admin/testimonials`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Testimonial added!');
      }
      
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setFormData({
      name: testimonial.name,
      instrument: testimonial.instrument,
      duration: testimonial.duration,
      text: testimonial.text,
      isVisible: testimonial.isVisible
    });
    setEditingId(testimonial.id);
    setShowAddForm(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/testimonials/${deleteDialog.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
    setDeleteDialog({ open: false, id: null });
  };

  const toggleVisibility = async (testimonial) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonial.id}`, 
        { isVisible: !testimonial.isVisible },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      toast.success(testimonial.isVisible ? 'Testimonial hidden' : 'Testimonial visible');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      instrument: 'Guitar',
      duration: '',
      text: '',
      isVisible: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  if (!isAuthenticated || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-100">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Music className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Guitar 101 Tuition</h1>
              <p className="text-sm text-gray-600">Testimonials Manager</p>
            </div>
          </div>
          <Button 
            onClick={() => window.location.href = '/admin'}
            variant="outline" 
            className="border-amber-600 text-amber-600 hover:bg-amber-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Header Actions */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Manage Testimonials</h2>
            <p className="text-gray-600 mt-1">Add, edit, or remove student testimonials</p>
          </div>
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Testimonial
          </Button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="mb-8 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingId ? 'Edit Testimonial' : 'Add New Testimonial'}</span>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Student Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Sarah Mitchell"
                      required
                      className="border-amber-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instrument">Instrument *</Label>
                    <select
                      id="instrument"
                      value={formData.instrument}
                      onChange={(e) => setFormData({...formData, instrument: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-amber-200"
                      required
                    >
                      <option value="Guitar">Guitar</option>
                      <option value="Bass">Bass</option>
                      <option value="Ukulele">Ukulele</option>
                    </select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration/Role *</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    placeholder="e.g., Student for 2 years"
                    required
                    className="border-amber-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="text">Testimonial Text *</Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => setFormData({...formData, text: e.target.value})}
                    placeholder="What did the student say about their experience?"
                    required
                    className="border-amber-200 min-h-[120px]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isVisible"
                    checked={formData.isVisible}
                    onChange={(e) => setFormData({...formData, isVisible: e.target.checked})}
                    className="w-4 h-4 text-amber-600"
                  />
                  <Label htmlFor="isVisible" className="cursor-pointer">
                    Visible on website
                  </Label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Update' : 'Add'} Testimonial
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Testimonials List */}
        {testimonials.length === 0 ? (
          <Card className="border-amber-100">
            <CardContent className="py-16 text-center">
              <Quote className="w-16 h-16 text-amber-200 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Testimonials Yet</h3>
              <p className="text-gray-500 mb-4">Add your first student testimonial to display on your website.</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Add First Testimonial
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id} 
                className={`border-amber-100 ${!testimonial.isVisible ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-200 to-amber-300 rounded-full flex items-center justify-center">
                          <span className="text-amber-700 font-bold">
                            {testimonial.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{testimonial.name}</h3>
                          <p className="text-sm text-amber-600">{testimonial.instrument} • {testimonial.duration}</p>
                        </div>
                        {!testimonial.isVisible && (
                          <span className="bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded">Hidden</span>
                        )}
                      </div>
                      <p className="text-gray-600 italic mt-2">"{testimonial.text}"</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleVisibility(testimonial)}
                        title={testimonial.isVisible ? 'Hide' : 'Show'}
                      >
                        {testimonial.isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(testimonial)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, id: testimonial.id })}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this testimonial? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TestimonialsManager;
